import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Users,
  Eye,
  MessageCircle,
  ShoppingCart,
  Clock,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  Target,
  Award,
  Zap,
  Heart,
  Share2,
  ExternalLink
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';

export default function AnalyticsDashboard({ affiliateId, userId }) {
  const { notify } = React.useContext(ToastContext);
  
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d, 1y
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);

  // Default empty analytics data structure
  const defaultAnalyticsData = {
    overview: {
      totalEarnings: 0,
      totalSales: 0,
      totalReferrals: 0,
      conversionRate: 0,
      avgOrderValue: 0,
      liveSessions: 0,
      totalViewers: 0,
      engagementRate: 0
    },
    earnings: {
      daily: [],
      monthly: []
    },
    liveSessions: [],
    topProducts: [],
    referrals: [],
    performance: {
      clickThroughRate: 0,
      conversionRate: 0,
      averageSessionDuration: '0m',
      bounceRate: 0,
      returnVisitorRate: 0
    }
  };

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, affiliateId]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // TODO: Implement real analytics data fetching
      // For now, show empty data structure to indicate no real data
      setAnalyticsData(defaultAnalyticsData);
      
      // Future implementation would call a Cloud Function like:
      // const getAffiliateAnalytics = httpsCallable(functions, 'getAffiliateAnalytics');
      // const result = await getAffiliateAnalytics({ 
      //   affiliateId, 
      //   userId, 
      //   timeRange 
      // });
      // setAnalyticsData(result.data);
      
    } catch (error) {
      console.error('Error loading analytics:', error);
      notify('Failed to load analytics data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (current < previous) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <BarChart3 className="w-4 h-4 text-gray-500" />;
  };

  const exportData = () => {
    notify('Exporting analytics data...', { type: 'info' });
    // Here you would implement actual data export
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  // Check if we have real data (not just empty defaults)
  const hasRealData = analyticsData.overview.totalEarnings > 0 || 
                     analyticsData.overview.totalSales > 0 || 
                     analyticsData.overview.totalReferrals > 0;

  return (
    <div className="space-y-6">
      {/* No Data Message */}
      {!hasRealData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-700">
            <BarChart3 className="w-5 h-5" />
            <span className="font-medium">Analytics Dashboard</span>
          </div>
          <p className="text-blue-600 mt-1">
            No analytics data available yet. Start promoting products to see your performance metrics here.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Track your affiliate performance and earnings</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button
            onClick={loadAnalyticsData}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={exportData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analyticsData.overview.totalEarnings)}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-1">
            {getTrendIcon(analyticsData.overview.totalEarnings, 12000)}
            <span className="text-sm text-green-600">+12.5% from last period</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sales</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analyticsData.overview.totalSales)}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-1">
            {getTrendIcon(analyticsData.overview.totalSales, 40000)}
            <span className="text-sm text-green-600">+8.2% from last period</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Referrals</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData.overview.totalReferrals}
              </p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-1">
            {getTrendIcon(analyticsData.overview.totalReferrals, 75)}
            <span className="text-sm text-green-600">+18.7% from last period</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPercentage(analyticsData.overview.conversionRate)}
              </p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <Target className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-1">
            {getTrendIcon(analyticsData.overview.conversionRate, 10.5)}
            <span className="text-sm text-green-600">+1.9% from last period</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Earnings Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Earnings Trend</h3>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Daily Earnings</span>
            </div>
          </div>
          <div className="h-64 flex items-end space-x-2">
            {analyticsData.earnings.daily.map((day, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-blue-500 rounded-t"
                  style={{ height: `${(day.amount / 1200) * 200}px` }}
                ></div>
                <span className="text-xs text-gray-600 mt-2">
                  {new Date(day.date).getDate()}
                </span>
                <span className="text-xs text-gray-500">
                  {formatCurrency(day.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Click-through Rate</span>
              <span className="font-semibold">{formatPercentage(analyticsData.performance.clickThroughRate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="font-semibold">{formatPercentage(analyticsData.performance.conversionRate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Session Duration</span>
              <span className="font-semibold">{analyticsData.performance.averageSessionDuration}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Bounce Rate</span>
              <span className="font-semibold">{formatPercentage(analyticsData.performance.bounceRate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Return Visitor Rate</span>
              <span className="font-semibold">{formatPercentage(analyticsData.performance.returnVisitorRate)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Live Sessions Analytics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Live Sessions Performance</h3>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Eye className="w-4 h-4" />
              <span>{analyticsData.overview.totalViewers} total viewers</span>
            </div>
            <div className="flex items-center space-x-1">
              <MessageCircle className="w-4 h-4" />
              <span>{analyticsData.overview.engagementRate}% engagement</span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {analyticsData.liveSessions.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Platform</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Duration</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Viewers</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Sales</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Revenue</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Commission</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.liveSessions.map((session) => (
                  <tr key={session.id} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900">{session.date}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{session.platform}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{session.duration}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{session.viewers}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{session.sales}</td>
                    <td className="py-3 px-4 text-sm font-medium text-green-600">
                      {formatCurrency(session.revenue)}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-blue-600">
                      {formatCurrency(session.commission)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatPercentage(session.engagement)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No live sessions recorded yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Products & Referrals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Products</h3>
          {analyticsData.topProducts.length > 0 ? (
            <div className="space-y-4">
              {analyticsData.topProducts.map((product, index) => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-600">{product.sales} sales</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{formatCurrency(product.commission)}</p>
                    <p className="text-sm text-green-600">{formatPercentage(product.conversionRate)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No product sales recorded yet</p>
            </div>
          )}
        </div>

        {/* Recent Referrals */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Referrals</h3>
          {analyticsData.referrals.length > 0 ? (
            <div className="space-y-4">
              {analyticsData.referrals.map((referral) => (
                <div key={referral.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">User {referral.user}</p>
                    <p className="text-sm text-gray-600">{referral.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{formatCurrency(referral.commission)}</p>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      referral.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {referral.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No referrals recorded yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
